// eslint-disable-next-line no-restricted-imports
import styled from '@emotion/styled';
import {
  Box,
  Flex,
  List,
  Modal,
  SearchField,
  Typography,
} from '@iheartjane/reefer';
import { spacing } from '@iheartjane/reefer-emotion';
import { useEffect, useState } from 'react';

import {
  useFetchRetailOrgs,
  useGetSelectedOrg,
  useRemoveSelectedOrg,
  useSetSelectedOrg,
} from '@jane/business-admin/data-access';
import type { RetailOrg } from '@jane/business-admin/types';

const StaticSearch = styled.div(({ theme }) => ({
  position: 'fixed',
  zIndex: 'var(--layers-visible)',
  background: theme.colors.background,
  width: '100%',
}));

const ScrollableContent = styled(Flex)({
  height: 'calc(100% - 180px)',
  overflowY: 'auto',
  paddingTop: 200,
  paddingBottom: 20,
  width: '100%',
});

const StyledBox = styled(Box)<{ selected: boolean }>(
  ({ selected, theme }) => ({
    background: selected ? theme.colors.brand.grape.light : 'transparent',
    '&:hover': {
      background: selected ? theme.colors.brand.grape.light : 'rgba(0,0,0,.1)',
    },
  }),
  {
    ...spacing({ py: 12, px: 24 }),
    width: '100%',
    height: '100%',
  }
);

export const RetailOrgModal = ({
  isOpen,
  requestClose,
}: {
  isOpen: boolean;
  requestClose: () => void;
}) => {
  const [modalOpen, setModalOpen] = useState(isOpen);
  const [searchQuery, setSearchQuery] = useState<string | undefined>(undefined);

  const { data: selectedOrg } = useGetSelectedOrg();
  const { mutate: setSelectedOrg } = useSetSelectedOrg();
  const { mutate: removeSelectedOrg } = useRemoveSelectedOrg();

  const { data: retailOrgs } = useFetchRetailOrgs({
    query: searchQuery,
  });

  const setSearchModalQuery = (query: string) => {
    setSearchQuery(query);
  };

  const onClickRetailOrg = (org: RetailOrg) => {
    setSelectedOrg(org);
    requestClose();
  };

  const onClearRetailOrg = () => {
    removeSelectedOrg();
    requestClose();
  };

  useEffect(() => {
    setModalOpen(isOpen);
  }, [isOpen]);

  return (
    <Modal
      variant="dialogue"
      open={modalOpen}
      overlayClose={true}
      onRequestClose={() => requestClose()}
    >
      <Modal.Content padding={false}>
        <Flex width={600}>
          <StaticSearch>
            <Flex flexDirection="column" px={24} my={24} width="100%">
              <Typography textAlign="center" variant="header-bold" mb={12}>
                Organization view
              </Typography>
              <Typography textAlign="center" mb={12}>
                Search and select an organization to manage.
                <br />
                You can change this anytime after the view loads.
              </Typography>
              <SearchField
                width="100%"
                label="Search by org name or store id/name"
                placeholder="Search by org name or store id/name"
                name="search_orgs"
                onChange={(val) => setSearchModalQuery(val)}
              />
            </Flex>
          </StaticSearch>
          <ScrollableContent>
            <List label="List of orgs">
              <List.Item
                key={`organization-none`}
                px={0}
                py={0}
                onClick={() => onClearRetailOrg()}
              >
                <StyledBox selected={!selectedOrg?.id}>All orgs</StyledBox>
              </List.Item>
              {retailOrgs?.organizations.map((org) => {
                return (
                  <List.Item
                    key={`organization-${org.id}`}
                    px={0}
                    py={0}
                    onClick={() => onClickRetailOrg(org)}
                  >
                    <StyledBox selected={selectedOrg?.id === org.id}>
                      {org.name}
                    </StyledBox>
                  </List.Item>
                );
              })}
            </List>
          </ScrollableContent>
        </Flex>
      </Modal.Content>
    </Modal>
  );
};
